import React from 'react';
import { Card, Table, Tag, Space, Button, message, Collapse, Switch, Popconfirm, Modal, Descriptions, Divider, Typography, QRCode, Row, Col, Checkbox, Alert, Select, Input } from 'antd';
import { EyeOutlined, QrcodeOutlined, CheckSquareOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
// Динамический импорт qrcode для совместимости

import { useAuthStore } from '../store/authStore';
import { ordersAPI } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  formatOrderDeliveryAddress,
  formatEmployeeName,
  getCustomerResponsibleContact,
  getCancelNegotiationListLabel,
  CANCEL_MIN_REASON_LEN,
  CANCEL_MAX_REASON_LEN
} from '../utils/orderDisplay';

const API_ORIGIN = process.env.REACT_APP_API_URL || 'https://api.luemen.ru';

const { Text } = Typography;

const { Panel } = Collapse;

const SupplierOrders: React.FC = () => {
  dayjs.locale('ru');
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);
  const [orders, setOrders] = React.useState<any[]>([]);
  const { user } = useAuthStore();
  const [checklistModal, setChecklistModal] = React.useState<{ visible: boolean; order: any | null }>({
    visible: false,
    order: null
  });
  const [viewOrderModal, setViewOrderModal] = React.useState<{ visible: boolean; order: any | null }>({
    visible: false,
    order: null
  });
  const [packingInProgress, setPackingInProgress] = React.useState<Set<number>>(new Set());
  const [cancelAgreeForOrderId, setCancelAgreeForOrderId] = React.useState<number | null>(null);
  const [cancelReasonDraft, setCancelReasonDraft] = React.useState('');

  React.useEffect(() => {
    if (user) {
      load();
    }
  }, [user]);

  const load = async () => {
    if (!user) {
      return;
    }
    if (!user.supplierId) {
      message.error('Вы не являетесь поставщиком');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ORIGIN}/api/orders?supplierId=${user.supplierId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (e: any) {
      message.error('Ошибка загрузки заказов');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChecklist = async (orderId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ORIGIN}/api/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const order = await response.json();
        setChecklistModal({ visible: true, order });
      } else {
        const error = await response.json();
        message.error(error.error || 'Не удалось загрузить заказ для чек-листа');
      }
    } catch (e) {
      message.error('Ошибка соединения с сервером');
    }
  };

  const mergeOrderEverywhere = (order: any) => {
    setViewOrderModal((prev) => (prev.order?.id === order.id ? { visible: prev.visible, order } : prev));
    setChecklistModal((prev) => (prev.order?.id === order.id ? { visible: prev.visible, order } : prev));
    setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, ...order } : o)));
  };

  const supplierNextStatusOptions = (current: string) => {
    if (current === 'pending') return [{ value: 'confirmed', label: 'Подтверждён' }];
    if (current === 'confirmed') return [{ value: 'in_preparation', label: 'Готовится' }];
    if (current === 'in_preparation') return [{ value: 'in_delivery', label: 'В доставке' }];
    return [];
  };

  const handleSupplierSetStatus = async (orderId: number, status: string) => {
    try {
      const updated = await ordersAPI.updateOrderStatus(orderId, status);
      message.success('Статус обновлён');
      mergeOrderEverywhere(updated);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось сменить статус');
    }
  };

  const handleCancelAgree = async (orderId: number, reason: string) => {
    const updated = await ordersAPI.agreeOrderCancel(orderId, reason);
    if (updated.status === 'cancelled') {
      message.success('Заказ отменён по согласию сторон');
    } else {
      message.info('Ваше согласие на отмену учтено. Ожидаем вторую сторону.');
    }
    mergeOrderEverywhere(updated);
    await load();
  };

  const openCancelAgreeModal = (orderId: number) => {
    setCancelReasonDraft('');
    setCancelAgreeForOrderId(orderId);
  };

  const submitCancelAgreeModal = async () => {
    if (cancelAgreeForOrderId == null) return;
    const t = cancelReasonDraft.trim();
    if (t.length < CANCEL_MIN_REASON_LEN) {
      message.warning(`Укажите причину отмены (не менее ${CANCEL_MIN_REASON_LEN} символов).`);
      return Promise.reject(new Error('reason-too-short'));
    }
    try {
      await handleCancelAgree(cancelAgreeForOrderId, t);
      setCancelAgreeForOrderId(null);
      setCancelReasonDraft('');
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось сохранить согласие');
      return Promise.reject(e);
    }
  };

  const handleOpenOrderDetails = async (orderId: number) => {
    try {
      const order = await ordersAPI.getOrder(orderId);
      setViewOrderModal({ visible: true, order });
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось загрузить заказ');
    }
  };

  const getPackingStats = (order: any) => {
    const items = order?.orderPool?.items || [];
    const total = items.length;
    const packed = items.filter((item: any) => item.isPacked).length;
    const percent = total ? Math.round((packed / total) * 100) : 0;
    return { total, packed, percent };
  };

  const applyPackingResult = (orderId: number, poolItemId: number, isPacked: boolean, payload: any) => {
    setOrders(prev => prev.map(order => {
      if (order.id !== orderId) return order;
      const nextOrder = { ...order, checklistVerified: payload.checklistVerified };
      if (order.orderPool?.items) {
        nextOrder.orderPool = {
          ...order.orderPool,
          items: order.orderPool.items.map((item: any) =>
            item.id === poolItemId
              ? { ...item, isPacked, packedAt: payload.item?.packedAt, packingMethod: payload.item?.packingMethod }
              : item
          )
        };
      }
      return nextOrder;
    }));

    setChecklistModal(prev => {
      if (!prev.order || prev.order.id !== orderId || !prev.order.orderPool) {
        return prev;
      }
      const updatedOrder = {
        ...prev.order,
        checklistVerified: payload.checklistVerified,
        orderPool: {
          ...prev.order.orderPool,
          items: prev.order.orderPool.items.map((item: any) =>
            item.id === poolItemId
              ? { ...item, isPacked, packedAt: payload.item?.packedAt, packingMethod: payload.item?.packingMethod }
              : item
          )
        }
      };
      return { ...prev, order: updatedOrder };
    });
  };

  const updatePackingStatus = async (orderId: number, poolItemId: number, isPacked: boolean, method: 'manual' | 'qr_print') => {
    const token = localStorage.getItem('token');
    if (!token) {
      message.error('Не найден токен авторизации');
      return;
    }

    setPackingInProgress(prev => {
      const next = new Set(prev);
      next.add(poolItemId);
      return next;
    });

    try {
      const response = await fetch(`${API_ORIGIN}/api/orders/${orderId}/checklist-items/${poolItemId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isPacked, method })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Не удалось обновить статус позиции');
      }

      const data = await response.json();
      applyPackingResult(orderId, poolItemId, isPacked, data);

      if (data.checklistVerified) {
        message.success('Все позиции упакованы — заказ отмечен как готовый');
      }

      return data;
    } catch (error: any) {
      message.error(error.message || 'Ошибка обновления статуса позиции');
      throw error;
    } finally {
      setPackingInProgress(prev => {
        const next = new Set(prev);
        next.delete(poolItemId);
        return next;
      });
    }
  };

  const updateComboPackingStatus = async (
    orderId: number,
    poolComboItemId: number,
    isPacked: boolean,
    method: 'manual' | 'qr_print'
  ) => {
    setPackingInProgress((prev) => {
      const next = new Set(prev);
      next.add(poolComboItemId);
      return next;
    });

    try {
      const response = await fetch(`${API_ORIGIN}/api/orders/${orderId}/checklist-combo-items/${poolComboItemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isPacked, method })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || 'Не удалось обновить статус упаковки комбо');
      }

      // Обновляем состояние чек-листа в модалке
      if (checklistModal.order) {
        setChecklistModal((prev) => {
          const order = prev.order;
          if (!order?.orderPool?.comboItems) return prev;
          const nextComboItems = order.orderPool.comboItems.map((it: any) =>
            it.id === poolComboItemId ? { ...it, isPacked: data.item.isPacked } : it
          );
          return {
            ...prev,
            order: {
              ...order,
              checklistVerified: data.checklistVerified,
              orderPool: {
                ...order.orderPool,
                comboItems: nextComboItems
              }
            }
          };
        });
      }
    } catch (e: any) {
      message.error(e?.message || 'Ошибка обновления упаковки комбо');
    } finally {
      setPackingInProgress((prev) => {
        const next = new Set(prev);
        next.delete(poolComboItemId);
        return next;
      });
    }
  };

  const handleToggleChecklistVerified = async (orderId: number, verified: boolean) => {
    const targetOrder = orders.find(order => order.id === orderId) 
      || (checklistModal.order?.id === orderId ? checklistModal.order : null);

    if (verified && targetOrder) {
      const stats = getPackingStats(targetOrder);
      if (stats.total > 0 && stats.packed < stats.total) {
        message.warning('Сначала отметьте упакованными все блюда в чек-листе');
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ORIGIN}/api/orders/${orderId}/checklist`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ verified })
      });

      if (response.ok) {
        message.success(verified ? 'Заказ отмечен как проверенный' : 'Проверка заказа отменена');
        load(); // Обновляем список заказов
        // Обновляем заказ в модальном окне, если оно открыто
        if (checklistModal.order && checklistModal.order.id === orderId) {
          setChecklistModal(prev => ({
            ...prev,
            order: { ...prev.order, checklistVerified: verified }
          }));
        }
      } else {
        const error = await response.json();
        message.error(error.error || 'Не удалось изменить статус проверки');
      }
    } catch (e) {
      message.error('Ошибка соединения с сервером');
    }
  };

  const capitalize = (text: string) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  /** День доставки: дата, на которую сотрудники заказчика выбрали блюда (пул / запись по сотруднику / поле API). */
  const getOrderDeliveryRaw = (order: any) =>
    order?.deliveryDate || order?.orderPool?.deliveryDate || order?.employeeOrderRecords?.[0]?.deliveryDate || null;

  /** Ячейка «день доставки» для шапки модалки (детали заказа). */
  const renderMenuDayTitleBox = (order: any) => {
    const raw = getOrderDeliveryRaw(order);
    const d = raw ? dayjs(raw) : null;
    const ok = !!(d && d.isValid());
    return (
      <div
        style={{
          minWidth: 200,
          maxWidth: 320,
          padding: '8px 14px',
          background: 'linear-gradient(180deg, #e6f7ff 0%, #f5fcff 100%)',
          border: '1px solid #91d5ff',
          borderRadius: 8
        }}
      >
        <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>День доставки</div>
        <div style={{ fontSize: 10, color: 'rgba(0,0,0,0.35)', marginTop: 2 }}>
          Дата выбора блюд сотрудниками
        </div>
        {ok ? (
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1677ff', marginTop: 6, lineHeight: 1.35 }}>
            {capitalize(d!.format('dddd, DD.MM.YYYY'))}
          </div>
        ) : (
          <Tag color="error" style={{ marginTop: 8 }}>
            Не указан
          </Tag>
        )}
      </div>
    );
  };

  /**
   * Полоса в теле модалки (чек-лист и т.д.).
   */
  const renderOrderDatesBanner = (order: any, opts?: { subtitle?: string }) => {
    const raw = getOrderDeliveryRaw(order);
    const d = raw ? dayjs(raw) : null;
    const deliveryOk = !!(d && d.isValid());
    return (
      <div
        style={{
          marginBottom: 20,
          padding: '14px 18px',
          background: 'linear-gradient(180deg, #e6f7ff 0%, #f5fcff 100%)',
          border: '1px solid #91d5ff',
          borderRadius: 8
        }}
      >
        {opts?.subtitle && (
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'rgba(0,0,0,0.75)' }}>
            {opts.subtitle}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>День доставки</div>
        <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.35)', marginTop: 2 }}>
          Дата, на которую сотрудники заказчика выбрали блюда
        </div>
        {deliveryOk ? (
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1677ff', marginTop: 4, lineHeight: 1.35 }}>
            {capitalize(d!.format('dddd, DD.MM.YYYY'))}
          </div>
        ) : (
          <Tag color="error" style={{ marginTop: 8 }}>
            Не указан — уточните у заказчика
          </Tag>
        )}
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 12 }}>
          Оформлен в системе: {new Date(order.createdAt).toLocaleString('ru')}
        </div>
      </div>
    );
  };

  const renderDeliveryColumn = (record: any) => {
    const raw = getOrderDeliveryRaw(record);
    if (!raw) {
      return <Tag color="error">Не указан</Tag>;
    }
    const d = dayjs(raw);
    return (
      <Space direction="vertical" size={0}>
        <Text strong style={{ color: '#1677ff' }}>{capitalize(d.format('dddd'))}</Text>
        <Text>{d.format('DD.MM.YYYY')}</Text>
      </Space>
    );
  };

  const formatTitle = (text?: string | null) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const printQRCode = async (poolItem: any, order: any) => {
    const printDate = new Date().toLocaleDateString('ru', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    try {
      await updatePackingStatus(order.id, poolItem.id, true, 'qr_print');
    } catch (error) {
      console.error('Не удалось автоматически отметить блюдо как упакованное', error);
    }
    
    // Получаем название компании заказчика из разных возможных источников
    const companyName = order.customer?.user?.companyName 
      || order.customer?.companyName 
      || order.customer?.name 
      || (order.customerId ? `Заказчик #${order.customerId}` : '');
    const supplierName = order.supplier?.user?.companyName 
      || order.supplier?.companyName 
      || 'Поставщик';
    const supplierLine = supplierName;
    const orderLine = `Заказ №${order.id}`;
    const rawDeliveryDate = order.deliveryDate || order.orderPool?.deliveryDate;
    const deliveryDateObj = rawDeliveryDate ? dayjs(rawDeliveryDate) : null;
    const deliveryDateText = deliveryDateObj ? capitalize(deliveryDateObj.format('dddd DD.MM.YYYY')) : '';
    const dishName = formatTitle(poolItem.menuItem?.name || 'Блюдо');
    const dishLine = dishName;
    const dateLine = deliveryDateText ? `На ${deliveryDateText}` : '';
    const employeeName = formatEmployeeName(poolItem.user);
    const customerLine = companyName ? `${employeeName} (${companyName})` : employeeName;
    
    const qrData = JSON.stringify({
      orderId: order.id,
      poolItemId: poolItem.id,
      menuItemId: poolItem.menuItemId,
      menuItemName: poolItem.menuItem?.name,
      quantity: poolItem.quantity,
      userId: poolItem.userId,
      username: employeeName,
      fullName: employeeName,
      customerId: order.customerId,
      supplierId: order.supplierId,
      deliveryDate: order.deliveryDate || order.orderPool?.deliveryDate,
      printDate: printDate,
      supplierName,
      supplierLine,
      dishLine,
      dateLine,
      deliveryDateText,
      customerLine,
      packagedAt: printDate,
      employeeNote: poolItem.employeeNote || null
    });

    try {
      // Динамически импортируем и генерируем QR-код в основном окне
      const QRCodeLib = await import('qrcode');
      const toDataURL = QRCodeLib.default?.toDataURL || QRCodeLib.toDataURL;
      const qrCodeDataURL = await toDataURL(qrData, {
        width: 250,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Открываем новое окно для печати с уже готовым QR-кодом
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>QR-код: ${poolItem.menuItem?.name || 'Блюдо'}</title>
              <style>
                @media print {
                  body { margin: 0; padding: 0; }
                  @page {
                    size: 58mm 40mm;
                    margin: 0;
                  }
                }
                body { 
                  display: flex; 
                  flex-direction: row; 
                  align-items: center; 
                  justify-content: center; 
                  margin: 0;
                  padding: 1.5mm;
                  font-family: Arial, sans-serif;
                  width: 58mm;
                  height: 40mm;
                  box-sizing: border-box;
                }
                .label { 
                  text-align: left; 
                  padding: 1.5mm;
                  width: 100%;
                  height: 100%;
                  box-sizing: border-box;
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  justify-content: space-around;
                  gap: 2mm;
                }
                .qr-section {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  flex-shrink: 0;
                }
                .info-section {
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  justify-content: center;
                  flex: 1;
                  min-width: 0;
                }
                h3 { margin: 0; font-size: 8px; font-weight: bold; line-height: 1.1; color: #000; }
                .info, .print-date { margin: 0.2mm 0; font-size: 8px; line-height: 1.1; color: #000; font-weight: 400; }
                .qr-container {
                  display: flex;
                  justify-content: center;
                  margin: 0;
                }
                .qr-image {
                  display: block;
                  width: 22mm;
                  height: 22mm;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                .username {
                  font-weight: bold;
                  color: #1890ff;
                  font-size: 8px;
                }
                .print-date {
                  margin-top: 0.3mm;
                }
              </style>
            </head>
            <body>
              <div class="label">
                <div class="qr-section">
                  <div class="qr-container">
                    <img src="${qrCodeDataURL}" alt="QR Code" class="qr-image" />
                  </div>
                </div>
                <div class="info-section">
                  <div class="info supplier-line">${supplierLine}</div>
                  <div class="info order-line">${orderLine}</div>
                  <h3>${dishLine}</h3>
                  ${dateLine ? `<div class="info date-line">${dateLine}</div>` : ''}
                  <div class="info">для <span class="username">${customerLine}</span></div>
                  <div class="info">Кол-во: ${poolItem.quantity} шт</div>
                  ${poolItem.employeeNote ? `<div class="info">Комм.: ${String(poolItem.employeeNote).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>` : ''}
                  <div class="print-date">Упаковка: ${printDate}</div>
                </div>
              </div>
              <script>
                // Ждем загрузки изображения, затем печатаем
                window.onload = function() {
                  var img = document.querySelector('.qr-image');
                  if (img) {
                    img.onload = function() {
                      setTimeout(function() {
                        window.print();
                      }, 300);
                    };
                    // Если изображение уже загружено
                    if (img.complete) {
                      setTimeout(function() {
                        window.print();
                      }, 300);
                    }
                  } else {
                    setTimeout(function() {
                      window.print();
                    }, 500);
                  }
                };
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      message.error('Ошибка генерации QR-кода');
    }
  };

  const printComboQRCode = async (poolComboItem: any, order: any) => {
    try {
      await updateComboPackingStatus(order.id, poolComboItem.id, true, 'qr_print');
    } catch {
      // ignore
    }

    const comboTitle = formatTitle(poolComboItem.titleSnapshot || poolComboItem.combo?.title || 'Комбо');
    const employeeName = formatEmployeeName(poolComboItem.user);
    const groups = Array.isArray(poolComboItem.selectionsSnapshot) ? poolComboItem.selectionsSnapshot : [];
    const composition = groups
      .map((g: any) => `${g.title}: ${(g.items || []).map((it: any) => it.name).join(', ')}`)
      .join(' | ');

    const qrData = JSON.stringify({
      orderId: order.id,
      poolComboItemId: poolComboItem.id,
      comboId: poolComboItem.comboId,
      comboTitle: comboTitle,
      quantity: poolComboItem.quantity,
      userId: poolComboItem.userId,
      username: employeeName,
      composition: composition || null,
      customerId: order.customerId,
      supplierId: order.supplierId,
      deliveryDate: order.deliveryDate || order.orderPool?.deliveryDate,
      printDate: new Date().toLocaleDateString('ru')
    });

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR-код: ${comboTitle}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 16px;
            }
            .title {
              font-size: 18px;
              font-weight: 700;
              margin-bottom: 8px;
            }
            .info {
              font-size: 14px;
              margin: 4px 0;
            }
          </style>
        </head>
        <body>
          <div class="title">${comboTitle}</div>
          <div class="info">Сотрудник: ${employeeName}</div>
          <div class="info">Кол-во: ${poolComboItem.quantity} шт</div>
          ${composition ? `<div class="info">Состав: ${String(composition).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>` : ''}
          <div style="margin-top: 12px;">
            <div id="qr"></div>
          </div>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></script>
          <script>
            new QRCode(document.getElementById("qr"), {
              text: ${JSON.stringify(qrData)},
              width: 180,
              height: 180
            });
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: 'orange',
      confirmed: 'blue',
      in_preparation: 'cyan',
      in_delivery: 'purple',
      delivered: 'green',
      cancelled: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: { [key: string]: string } = {
      pending: 'Ожидает',
      confirmed: 'Подтвержден',
      in_preparation: 'Готовится',
      in_delivery: 'В доставке',
      delivered: 'Доставлен',
      cancelled: 'Отменен'
    };
    return texts[status] || status;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70
    },
    {
      title: 'Создан',
      dataIndex: 'createdAt',
      width: 110,
      render: (date: string) => new Date(date).toLocaleString('ru', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    {
      title: 'День доставки',
      key: 'deliveryDate',
      width: 160,
      render: (_: any, record: any) => renderDeliveryColumn(record)
    },
    {
      title: 'Заказчик',
      render: (record: any) =>
        record.customer?.user?.companyName ||
        record.customer?.companyName ||
        '-'
    },
    {
      title: 'Сумма',
      dataIndex: 'totalAmount',
      render: (amount: number) => `₽${amount}`
    },
    {
      title: 'Статус',
      key: 'orderStatus',
      render: (_: unknown, record: any) => {
        const neg = getCancelNegotiationListLabel(record);
        return (
          <Space direction="vertical" size={4}>
            <Tag color={getStatusColor(record.status)}>{getStatusText(record.status)}</Tag>
            {neg ? (
              <Tag color="volcano" style={{ margin: 0 }}>
                {neg}
              </Tag>
            ) : null}
          </Space>
        );
      }
    },
    {
      title: 'Готовность',
      render: (record: any) => {
        const stats = getPackingStats(record);
        if (!stats.total) {
          return <Tag color="default">Нет позиций</Tag>;
        }
        const ready = stats.packed === stats.total;
        return (
          <Space size={0} direction="vertical">
            <Tag color={ready ? 'green' : 'blue'}>
              {stats.packed}/{stats.total}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {stats.percent}%
            </Text>
          </Space>
        );
      }
    },
    {
      title: 'Проверен',
      dataIndex: 'checklistVerified',
      render: (verified: boolean, record: any) => (
        <Popconfirm
          title={verified ? 'Отменить проверку заказа?' : 'Отметить заказ как проверенный?'}
          onConfirm={() => handleToggleChecklistVerified(record.id, !verified)}
          okText="Да"
          cancelText="Нет"
        >
          <Switch
            checked={verified}
            checkedChildren="Да"
            unCheckedChildren="Нет"
            size="small"
          />
        </Popconfirm>
      )
    },
    {
      title: 'Действия',
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleOpenOrderDetails(record.id)}
          >
            Детали
          </Button>
          <Button 
            icon={<QrcodeOutlined />} 
            size="small"
            onClick={() => message.info('Генерация QR в разработке')}
          >
            QR
          </Button>
          <Button 
            icon={<CheckSquareOutlined />} 
            size="small"
            type={record.checklistVerified ? 'default' : 'primary'}
            onClick={() => handleOpenChecklist(record.id)}
          >
            Чек-лист
          </Button>
        </Space>
      )
    }
  ];

  const modalPackingStats = checklistModal.order ? getPackingStats(checklistModal.order) : null;

  const showNoDeliveryDateHint =
    !loading &&
    orders.length > 0 &&
    orders.every((o) => !getOrderDeliveryRaw(o));

  return (
    <div style={{ display: 'contents' }}>
      <Card 
        title="Заказы организаций" 
        loading={loading}
        extra={
          <Space>
            <Tag color="blue">Всего заказов: {orders.length}</Tag>
          </Space>
        }
      >
        {showNoDeliveryDateHint && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="День доставки не указан ни в одном заказе"
            description="Так бывает у старых заказов без привязки к пулу меню. В новых заказах это дата, на которую сотрудники заказчика выбрали блюда — она показывается в колонке «День доставки», в деталях и в чек-листе."
          />
        )}
        <Table
          rowKey="id"
          columns={columns as any}
          dataSource={orders}
          pagination={{ 
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Всего: ${total}`
          }}
          locale={{ emptyText: 'Нет заказов' }}
          scroll={isMobile ? { x: 'max-content' } : undefined}
          size={isMobile ? 'small' : 'middle'}
          expandable={{
            expandedRowRender: (record) => {
              const rc = getCustomerResponsibleContact(record);
              return (
              <div style={{ padding: '8px 16px' }}>
                <p style={{ fontSize: 15, marginBottom: 12 }}>
                  <b>День доставки</b>{' '}
                  <span style={{ color: 'rgba(0,0,0,0.45)', fontWeight: 400 }}>(когда выбраны блюда)</span>{': '}
                  {getOrderDeliveryRaw(record) ? (
                    <Tag color="blue" style={{ fontSize: 14 }}>
                      {capitalize(dayjs(getOrderDeliveryRaw(record)).format('dddd, DD.MM.YYYY'))}
                    </Tag>
                  ) : (
                    <Tag color="error">Не указан — уточните у заказчика</Tag>
                  )}
                </p>
                <p>
                  <b>Адрес доставки:</b>{' '}
                  {formatOrderDeliveryAddress(record.deliveryAddress) || 'Не указан'}
                </p>
                <p>
                  <b>Ответственный с заказчика:</b> {rc.fullName || '—'}
                  {rc.position ? ` (${rc.position})` : ''}
                  <br />
                  <b>Телефон:</b> {rc.phone || '—'}
                </p>
                <p><b>Позиции заказа:</b></p>
                {record.items?.map((item: any, idx: number) => (
                  <div key={idx} style={{ marginLeft: 16 }}>
                    • {item.menuItem?.name || 'Блюдо'} x{item.quantity} = ₽{item.price * item.quantity}
                  </div>
                ))}
              </div>
              );
            },
          }}
        />
      </Card>

      <Modal
        title={
          viewOrderModal.order ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 16,
                flexWrap: 'wrap',
                width: '100%',
                paddingRight: 40
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 600 }}>Заказ №{viewOrderModal.order.id}</span>
              {renderMenuDayTitleBox(viewOrderModal.order)}
            </div>
          ) : (
            'Детали заказа'
          )
        }
        open={viewOrderModal.visible}
        onCancel={() => setViewOrderModal({ visible: false, order: null })}
        footer={[
          <Button key="close" onClick={() => setViewOrderModal({ visible: false, order: null })}>
            Закрыть
          </Button>
        ]}
        width={720}
      >
        {viewOrderModal.order && (
          <div>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Оформлен в системе">
                {new Date(viewOrderModal.order.createdAt).toLocaleString('ru')}
              </Descriptions.Item>
              <Descriptions.Item label="Заказчик">
                {viewOrderModal.order.customer?.user?.companyName ||
                  viewOrderModal.order.customer?.companyName ||
                  `ID ${viewOrderModal.order.customerId}`}
              </Descriptions.Item>
              <Descriptions.Item label="Адрес доставки">
                {formatOrderDeliveryAddress(viewOrderModal.order.deliveryAddress) || 'Не указан'}
              </Descriptions.Item>
              {(() => {
                const rc = getCustomerResponsibleContact(viewOrderModal.order);
                return (
                  <>
                    <Descriptions.Item label="Ответственный (ФИО)">
                      {rc.fullName || '—'}
                      {rc.position ? (
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                          {rc.position}
                        </Text>
                      ) : null}
                    </Descriptions.Item>
                    <Descriptions.Item label="Телефон ответственного">{rc.phone || '—'}</Descriptions.Item>
                  </>
                );
              })()}
              <Descriptions.Item label="Сумма">₽{viewOrderModal.order.totalAmount}</Descriptions.Item>
              <Descriptions.Item label="Статус">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                  <Space wrap align="center">
                    <Tag color={getStatusColor(viewOrderModal.order.status)}>
                      {getStatusText(viewOrderModal.order.status)}
                    </Tag>
                    {supplierNextStatusOptions(viewOrderModal.order.status).length > 0 && (
                      <Select
                        placeholder="Следующий этап"
                        style={{ minWidth: 220 }}
                        options={supplierNextStatusOptions(viewOrderModal.order.status)}
                        onChange={(v) => handleSupplierSetStatus(viewOrderModal.order.id, v)}
                      />
                    )}
                  </Space>
                  {getCancelNegotiationListLabel(viewOrderModal.order) ? (
                    <Tag color="volcano" style={{ margin: 0 }}>
                      {getCancelNegotiationListLabel(viewOrderModal.order)}
                    </Tag>
                  ) : null}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Состав">
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {viewOrderModal.order.items?.map((item: any) => (
                    <li key={item.id}>
                      {item.menuItem?.name || 'Блюдо'} × {item.quantity} — ₽{(item.price * item.quantity).toFixed(2)}
                    </li>
                  ))}
                </ul>
              </Descriptions.Item>
            </Descriptions>
            {viewOrderModal.order.status !== 'delivered' && viewOrderModal.order.status !== 'cancelled' && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
                message="Согласование отмены заказа"
                description={
                  <Space direction="vertical" size="small">
                    <div>
                      Заказчик:{' '}
                      {viewOrderModal.order.cancelCustomerAgreed ? (
                        <Tag color="success">согласен</Tag>
                      ) : (
                        <Tag>ожидается</Tag>
                      )}
                    </div>
                    <div>
                      Поставщик:{' '}
                      {viewOrderModal.order.cancelSupplierAgreed ? (
                        <Tag color="success">согласен</Tag>
                      ) : (
                        <Tag>ожидается</Tag>
                      )}
                    </div>
                    {viewOrderModal.order.cancelReasonCustomer ? (
                      <div>
                        <Text type="secondary">Причина (заказчик): </Text>
                        <Text>{viewOrderModal.order.cancelReasonCustomer}</Text>
                      </div>
                    ) : null}
                    {viewOrderModal.order.cancelReasonSupplier ? (
                      <div>
                        <Text type="secondary">Причина (поставщик): </Text>
                        <Text>{viewOrderModal.order.cancelReasonSupplier}</Text>
                      </div>
                    ) : null}
                    {!viewOrderModal.order.cancelSupplierAgreed && (
                      <Button onClick={() => openCancelAgreeModal(viewOrderModal.order.id)}>
                        Подтвердить согласие на отмену
                      </Button>
                    )}
                  </Space>
                }
              />
            )}
            {viewOrderModal.order.status === 'cancelled' &&
              (viewOrderModal.order.cancelReasonCustomer ||
                viewOrderModal.order.cancelReasonSupplier ||
                viewOrderModal.order.cancelReasonAdmin) && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                  message="Причины отмены"
                  description={
                    <Space direction="vertical" size="small">
                      {viewOrderModal.order.cancelReasonCustomer ? (
                        <div>
                          <Text type="secondary">Заказчик: </Text>
                          <Text>{viewOrderModal.order.cancelReasonCustomer}</Text>
                        </div>
                      ) : null}
                      {viewOrderModal.order.cancelReasonSupplier ? (
                        <div>
                          <Text type="secondary">Поставщик: </Text>
                          <Text>{viewOrderModal.order.cancelReasonSupplier}</Text>
                        </div>
                      ) : null}
                      {viewOrderModal.order.cancelReasonAdmin ? (
                        <div>
                          <Text type="secondary">Администратор: </Text>
                          <Text>{viewOrderModal.order.cancelReasonAdmin}</Text>
                        </div>
                      ) : null}
                    </Space>
                  }
                />
              )}
          </div>
        )}
      </Modal>

      {/* Модальное окно чек-листа */}
      <Modal
        title={
          checklistModal.order ? (
            <Space wrap align="center">
              <CheckSquareOutlined />
              <span style={{ fontWeight: 600 }}>Чек-лист заказа #{checklistModal.order.id}</span>
              {modalPackingStats && modalPackingStats.total > 0 && (
                <Tag color={modalPackingStats.packed === modalPackingStats.total ? 'green' : 'blue'}>
                  {modalPackingStats.packed}/{modalPackingStats.total} блюд
                </Tag>
              )}
              <Switch
                checked={checklistModal.order.checklistVerified}
                checkedChildren="Проверен"
                unCheckedChildren="Не проверен"
                onChange={(checked) => handleToggleChecklistVerified(checklistModal.order.id, checked)}
              />
            </Space>
          ) : (
            <Space>
              <CheckSquareOutlined />
              <span>Чек-лист</span>
            </Space>
          )
        }
        open={checklistModal.visible}
        onCancel={() => setChecklistModal({ visible: false, order: null })}
        footer={[
          <Button key="print" icon={<PrinterOutlined />} onClick={() => {
            const printContent = document.getElementById('checklist-content');
            if (printContent) {
              const printWindow = window.open('', '_blank');
              if (printWindow) {
                printWindow.document.write(`
                  <!DOCTYPE html>
                  <html>
                    <head>
                      <title>Чек-лист заказа #${checklistModal.order?.id}</title>
                      <style>
                        @media print {
                          body { margin: 0; }
                          @page { margin: 15mm; }
                          .no-print { display: none !important; }
                        }
                        body {
                          font-family: Arial, sans-serif;
                          padding: 20px;
                          line-height: 1.4;
                        }
                        table {
                          width: 100%;
                          border-collapse: collapse;
                          margin: 20px 0;
                        }
                        th, td {
                          border: 1px solid #ddd;
                          padding: 8px;
                          text-align: left;
                          line-height: 1.4;
                        }
                        th {
                          background-color: #f2f2f2;
                          font-weight: bold;
                        }
                        .header {
                          text-align: center;
                          border-bottom: 2px solid #1890ff;
                          padding-bottom: 16px;
                          margin-bottom: 24px;
                        }
                        .info-card {
                          border: 1px solid #ddd;
                          padding: 12px;
                          margin: 12px 0;
                        }
                        .total {
                          font-size: 18px;
                          font-weight: bold;
                          color: #1890ff;
                        }
                      </style>
                    </head>
                    <body>
                      ${printContent.innerHTML}
                    </body>
                  </html>
                `);
                printWindow.document.close();
                setTimeout(() => {
                  printWindow.print();
                }, 500);
              }
            }
          }}>
            Печать заказа
          </Button>,
          <Button key="close" onClick={() => setChecklistModal({ visible: false, order: null })}>
            Закрыть
          </Button>
        ]}
        width={isMobile ? '100%' : 1200}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
      >
        {checklistModal.order && (() => {
          const checklistDeliveryAt = getOrderDeliveryRaw(checklistModal.order);
          return (
          <div id="checklist-content" style={{ padding: '20px 0' }}>
            {renderOrderDatesBanner(checklistModal.order, {
              subtitle: `Чек-лист заказа №${checklistModal.order.id}`
            })}
            {!checklistDeliveryAt && (
              <Alert
                type="warning"
                showIcon
                message="В заказе не указан день доставки"
                description="Это дата, на которую сотрудники заказчика выбрали блюда. Без неё неясно, на какой день доставки собран заказ — уточните у заказчика."
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Информация о заказчике и поставщике */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Card size="small" title="Заказчик">
                  <Text strong>{checklistModal.order.customer?.user?.companyName || checklistModal.order.customer?.companyName || `ID: ${checklistModal.order.customerId}`}</Text>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                      Адрес: {formatOrderDeliveryAddress(checklistModal.order.deliveryAddress) || 'Не указан'}
                    </Text>
                  </div>
                  {(() => {
                    const rc = getCustomerResponsibleContact(checklistModal.order);
                    return (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary">
                          Ответственный: {rc.fullName || '—'}
                          {rc.position ? ` (${rc.position})` : ''}
                        </Text>
                        <br />
                        <Text type="secondary">Тел.: {rc.phone || '—'}</Text>
                      </div>
                    );
                  })()}
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Поставщик">
                  <Text strong>
                    {checklistModal.order.supplier?.user?.companyName 
                      || checklistModal.order.supplier?.companyName 
                      || 'Поставщик'}
                  </Text>
                </Card>
              </Col>
            </Row>

            {/* Таблица с блюдами */}
            <Table
              dataSource={checklistModal.order.items || []}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: '№',
                  key: 'index',
                  width: 50,
                  render: (_: any, __: any, index: number) => index + 1
                },
                {
                  title: 'Блюдо',
                  dataIndex: ['menuItem', 'name'],
                  key: 'name',
                  render: (name: string) => (
                    <Text strong>{name || 'Блюдо'}</Text>
                  )
                },
                {
                  title: 'Количество',
                  dataIndex: 'quantity',
                  key: 'quantity',
                  width: 100,
                  align: 'center',
                  render: (qty: number) => <Text strong>{qty} шт</Text>
                },
                {
                  title: 'Цена за единицу',
                  dataIndex: 'price',
                  key: 'price',
                  width: 120,
                  align: 'right',
                  render: (price: number) => `${price.toFixed(2)} ₽`
                },
                {
                  title: 'Сумма',
                  key: 'total',
                  width: 120,
                  align: 'right',
                  render: (_: any, record: any) => (
                    <Text strong style={{ fontSize: 14 }}>
                      {(record.price * record.quantity).toFixed(2)} ₽
                    </Text>
                  )
                }
              ]}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4} align="right">
                      <Text strong style={{ fontSize: 16 }}>Итого:</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                        {checklistModal.order.totalAmount?.toFixed(2) || '0.00'} ₽
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />

            {/* QR-коды для печати по сотрудникам */}
            {checklistModal.order.orderPool &&
              ((checklistModal.order.orderPool.items && checklistModal.order.orderPool.items.length > 0) ||
                (checklistModal.order.orderPool.comboItems && checklistModal.order.orderPool.comboItems.length > 0)) && (
              <div>
                <Divider>QR-коды для печати (блюда)</Divider>
                <Table
                  dataSource={checklistModal.order.orderPool.items || []}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: '№',
                      key: 'index',
                      width: 50,
                      render: (_: any, __: any, index: number) => index + 1
                    },
                    {
                      title: 'Сотрудник',
                      key: 'employee',
                      render: (_: any, record: any) => (
                        <Text strong>{formatEmployeeName(record.user)}</Text>
                      )
                    },
                    {
                      title: 'Блюдо',
                      dataIndex: ['menuItem', 'name'],
                      key: 'dish',
                      render: (name: string) => <Text strong>{name}</Text>
                    },
                    {
                      title: 'Комментарий',
                      key: 'note',
                      ellipsis: true,
                      render: (_: any, r: any) =>
                        r.employeeNote ? <Text type="secondary">{r.employeeNote}</Text> : '—'
                    },
                    {
                      title: 'Количество',
                      dataIndex: 'quantity',
                      key: 'quantity',
                      align: 'center',
                      render: (qty: number) => <Text strong>{qty} шт</Text>
                    },
                    {
                      title: 'Цена',
                      dataIndex: ['menuItem', 'price'],
                      key: 'price',
                      align: 'right',
                      render: (price: number) => `${price?.toFixed(2) || '0.00'} ₽`
                    },
                  {
                    title: 'Упаковано',
                    key: 'packed',
                    width: 160,
                    align: 'center',
                    render: (_: any, poolItem: any) => (
                      <Checkbox
                        checked={!!poolItem.isPacked}
                        onChange={(e) => updatePackingStatus(checklistModal.order!.id, poolItem.id, e.target.checked, 'manual')}
                        disabled={packingInProgress.has(poolItem.id)}
                      >
                        {poolItem.isPacked ? 'Да' : 'Нет'}
                      </Checkbox>
                    )
                  },
                    {
                      title: 'QR-код',
                      key: 'qr',
                      width: 150,
                      align: 'center',
                      render: (_: any, poolItem: any) => {            
                        const employeeName = formatEmployeeName(poolItem.user);
                        const qrData = JSON.stringify({
                          orderId: checklistModal.order.id,
                          poolItemId: poolItem.id,
                          menuItemId: poolItem.menuItemId,
                          menuItemName: poolItem.menuItem?.name,
                          quantity: poolItem.quantity,
                          userId: poolItem.userId,
                          username: employeeName,
                          fullName: employeeName,
                          employeeNote: poolItem.employeeNote || null,
                          customerId: checklistModal.order.customerId,
                          supplierId: checklistModal.order.supplierId,
                          deliveryDate:
                            checklistModal.order.deliveryDate ||
                            checklistModal.order.orderPool?.deliveryDate,
                          printDate: new Date().toLocaleDateString('ru')
                        });
                        
                        return (
                          <div style={{ textAlign: 'center' }}>
                            <QRCode 
                              value={qrData}
                              size={80}
                              style={{ marginBottom: 4 }}
                            />
                            <div>
                              <Button
                                size="small"
                                icon={<PrinterOutlined />}
                                onClick={() => printQRCode(poolItem, checklistModal.order)}
                                type="primary"
                                loading={packingInProgress.has(poolItem.id)}
                              >
                                Печать
                              </Button>
                            </div>
                          </div>
                        );
                      }
                    }
                  ]}
                />

                {checklistModal.order.orderPool.comboItems && checklistModal.order.orderPool.comboItems.length > 0 && (
                  <>
                    <Divider>QR-коды для печати (комбо)</Divider>
                    <Table
                      dataSource={checklistModal.order.orderPool.comboItems || []}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: '№',
                          key: 'index',
                          width: 50,
                          render: (_: any, __: any, index: number) => index + 1
                        },
                        {
                          title: 'Сотрудник',
                          key: 'employee',
                          render: (_: any, record: any) => (
                            <Text strong>{formatEmployeeName(record.user)}</Text>
                          )
                        },
                        {
                          title: 'Комбо / состав',
                          key: 'combo',
                          render: (_: any, r: any) => {
                            const groups = Array.isArray(r.selectionsSnapshot) ? r.selectionsSnapshot : [];
                            return (
                              <Space direction="vertical" size={2}>
                                <Text strong>{r.titleSnapshot || r.combo?.title || 'Комбо'}</Text>
                                {groups.length > 0 ? (
                                  <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)' }}>
                                    {groups.map((g: any, gi: number) => (
                                      <div key={gi}>
                                        <Text type="secondary">{g.title}: </Text>
                                        {(g.items || []).map((it: any) => it.name).filter(Boolean).join(', ') || '—'}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    Состав не указан
                                  </Text>
                                )}
                              </Space>
                            );
                          }
                        },
                        {
                          title: 'Количество',
                          dataIndex: 'quantity',
                          key: 'quantity',
                          align: 'center',
                          render: (qty: number) => <Text strong>{qty} шт</Text>
                        },
                        {
                          title: 'Упаковано',
                          key: 'packed',
                          width: 160,
                          align: 'center',
                          render: (_: any, poolComboItem: any) => (
                            <Checkbox
                              checked={!!poolComboItem.isPacked}
                              onChange={(e) =>
                                updateComboPackingStatus(
                                  checklistModal.order!.id,
                                  poolComboItem.id,
                                  e.target.checked,
                                  'manual'
                                )
                              }
                              disabled={packingInProgress.has(poolComboItem.id)}
                            >
                              {poolComboItem.isPacked ? 'Да' : 'Нет'}
                            </Checkbox>
                          )
                        },
                        {
                          title: 'QR-код',
                          key: 'qr',
                          width: 150,
                          align: 'center',
                          render: (_: any, poolComboItem: any) => (
                            <div style={{ textAlign: 'center' }}>
                              <QRCode
                                value={JSON.stringify({
                                  orderId: checklistModal.order.id,
                                  poolComboItemId: poolComboItem.id,
                                  comboId: poolComboItem.comboId,
                                  comboTitle: poolComboItem.titleSnapshot || poolComboItem.combo?.title,
                                  quantity: poolComboItem.quantity,
                                  userId: poolComboItem.userId,
                                  username: formatEmployeeName(poolComboItem.user),
                                  fullName: formatEmployeeName(poolComboItem.user),
                                  customerId: checklistModal.order.customerId,
                                  supplierId: checklistModal.order.supplierId,
                                  deliveryDate:
                                    checklistModal.order.deliveryDate ||
                                    checklistModal.order.orderPool?.deliveryDate,
                                  printDate: new Date().toLocaleDateString('ru')
                                })}
                                size={80}
                                style={{ marginBottom: 4 }}
                              />
                              <div>
                                <Button
                                  size="small"
                                  icon={<PrinterOutlined />}
                                  onClick={() => printComboQRCode(poolComboItem, checklistModal.order)}
                                  type="primary"
                                  loading={packingInProgress.has(poolComboItem.id)}
                                >
                                  Печать
                                </Button>
                              </div>
                            </div>
                          )
                        }
                      ]}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        );
        })()}
      </Modal>

      <Modal
        title="Согласие на отмену заказа"
        open={cancelAgreeForOrderId !== null}
        okText="Отправить"
        cancelText="Закрыть"
        onOk={submitCancelAgreeModal}
        onCancel={() => {
          setCancelAgreeForOrderId(null);
          setCancelReasonDraft('');
        }}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Опишите причину отмены (не менее {CANCEL_MIN_REASON_LEN} символов, не более{' '}
          {CANCEL_MAX_REASON_LEN}). Без текста отправить нельзя.
        </Text>
        <Input.TextArea
          rows={4}
          maxLength={CANCEL_MAX_REASON_LEN}
          showCount
          value={cancelReasonDraft}
          onChange={(e) => setCancelReasonDraft(e.target.value)}
          placeholder="Например: не успеваем приготовить к указанному времени…"
        />
      </Modal>
    </div>
  );
};

export default SupplierOrders;

